/**
 * One vocabulary for the release read model, shared by every surface.
 *
 * The API speaks in exact codes because they are evidence. A person reads a
 * sentence. The code stays reachable — in a title and a data attribute — so a
 * screenshot can still be matched to the server's answer.
 */
import type { BadgeTone } from "@/design-system/StatusBadge";
import type { ReleaseStatusResponse } from "@/shared/api";

export type Verdict = { readonly tone: BadgeTone; readonly text: string };

export const RELEASE_STATUS: Record<ReleaseStatusResponse["status"], Verdict> = {
  UP_TO_DATE: { tone: "ready", text: "Релиз актуален" },
  NEWER_MAIN_AVAILABLE: { tone: "active", text: "В main есть более новая версия" },
  RELEASE_DRIFT: { tone: "danger", text: "Установка расходится с релизом" },
  UNKNOWN: { tone: "warning", text: "Состояние релиза не подтверждено" },
};

export const COMPATIBILITY: Record<ReleaseStatusResponse["component_compatibility"], Verdict> = {
  COMPATIBLE: { tone: "ready", text: "Совместимы" },
  INCOMPATIBLE: { tone: "danger", text: "Несовместимы" },
  UNKNOWN: { tone: "warning", text: "Не подтверждено" },
};

export const RUNTIME_IDENTITY: Record<ReleaseStatusResponse["runtime_identity_status"], Verdict> = {
  VERIFIED: { tone: "ready", text: "Установлен и проверен" },
  DRIFT: { tone: "danger", text: "Отличается от записи об установке" },
  UNKNOWN: { tone: "warning", text: "Не подтверждено" },
};

export const HEALTH: Record<ReleaseStatusResponse["health_status"], Verdict> = {
  HEALTHY: { tone: "ready", text: "Работает" },
  DEGRADED: { tone: "warning", text: "Работает с ограничениями" },
  UNKNOWN: { tone: "warning", text: "Не подтверждено" },
};

const REASON: Readonly<Record<string, string>> = {
  MATCHED_DEPLOYMENT_RECORD: "совпадает с записью об установке",
  RUNTIME_RECORD_INVALID: "запись об установке не читается",
  RUNTIME_RECORD_MISSING: "записи об установке нет",
  RUNTIME_OBSERVATION_UNAVAILABLE: "установленный код недоступен для проверки",
  RUNTIME_RECORD_CHECKOUT_MISMATCH: "установленный код отличается от записи",
  RUNTIME_COMPATIBILITY_EVIDENCE_MISSING: "нет данных для проверки совместимости",
  RUNTIME_RELEASE_LINEAGE_MISMATCH: "установлен код другого релиза",
  RUNTIME_RELEASE_ARTIFACT_MISMATCH: "сборка отличается от релиза",
  RUNTIME_RELEASE_CONTRACT_MISMATCH: "политики стратегии отличаются от релиза",
  RUNTIME_DATABASE_SCHEMA_MISMATCH: "схема базы отличается от релиза",
  MATCHED_RELEASE_CONTRACTS_AND_SCHEMA: "политики и схема базы совпадают",
};

/** A code the vocabulary does not know is shown as itself, never hidden. */
export function reason(code: string | null | undefined): string {
  if (code === null || code === undefined) {
    return "—";
  }
  return REASON[code] ?? code;
}

const RELEASE_CLASS: Readonly<Record<string, string>> = {
  TRADING_AUTHORITY_CHANGE: "Изменение торговых полномочий",
  NON_TRADING_DEPLOYABLE: "Без изменения торговли",
};

export function releaseClass(code: string | null): string {
  return code === null ? "—" : (RELEASE_CLASS[code] ?? code);
}

export function provenance(code: ReleaseStatusResponse["runtime_provenance"]): string {
  if (code === "SIGNED_RELEASE") {
    return "Подписанный релиз";
  }
  if (code === "OWNER_CONFIRMED_OBSERVATION") {
    return "Подтверждено владельцем при установке";
  }
  return "—";
}

/** Short enough to read, exact in the title for anyone who needs to compare. */
export function shortSha(sha: string | null): string {
  return sha === null ? "—" : sha.slice(0, 8);
}

/**
 * The single sentence above everything else. Calm when calm; the worst fact
 * first when not, because the reader came to find out whether to worry.
 */
export function releaseVerdict(release: ReleaseStatusResponse): Verdict {
  if (release.status === "RELEASE_DRIFT") {
    return RELEASE_STATUS.RELEASE_DRIFT;
  }
  if (release.runtime_identity_status !== "VERIFIED") {
    return {
      tone: release.runtime_identity_status === "DRIFT" ? "danger" : "warning",
      text: `Торговый runtime: ${reason(release.runtime_identity_reason)}`,
    };
  }
  return RELEASE_STATUS[release.status];
}
